/**
 * Creates a batcher that accumulates individual load() calls within a time
 * window and resolves them with a single batched call.
 */
export function createBatcher<K, V>(
  batchFn: (keys: K[]) => Promise<Map<K, V>>,
  windowMs: number
) {
  let pending: Array<{
    key: K;
    resolve: (value: V | undefined) => void;
    reject: (error: unknown) => void;
  }> = [];
  let timer: ReturnType<typeof setTimeout> | null = null;

  async function flush() {
    const batch = pending;
    pending = [];
    timer = null;

    const keys = Array.from(new Set(batch.map((b) => b.key)));
    try {
      const results = await batchFn(keys);
      for (const { key, resolve } of batch) {
        resolve(results.get(key));
      }
    } catch (error) {
      for (const { reject } of batch) {
        reject(error);
      }
    }
  }

  return {
    load(key: K): Promise<V | undefined> {
      return new Promise((resolve, reject) => {
        pending.push({ key, resolve, reject });
        if (!timer) {
          timer = setTimeout(flush, windowMs);
        }
      });
    },
  };
}
